import type React from "react";
import { useTheme } from "../ThemeProvider/ThemeProvider";
import styles from "./themeToggle.module.scss";

export interface ThemeToggleProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  ariaLabel?: string;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = "",
  size = "md",
  showLabel = false,
  ariaLabel,
}) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  const defaultAriaLabel = `Switch to ${isDark ? "light" : "dark"} mode`;
  const finalAriaLabel = ariaLabel || defaultAriaLabel;

  return (
    <button
      type="button"
      className={`${styles.themeToggle} ${styles[size]} ${className}`}
      onClick={toggleTheme}
      aria-label={finalAriaLabel}
      aria-pressed={isDark}
    >
      <span className={styles.iconContainer}>
        {/* Sun Icon */}
        <svg
          className={`${styles.icon} ${styles.sunIcon} ${
            !isDark ? styles.active : ""
          }`}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="3" fill="currentColor" />
          <path
            d="M12 5V3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M12 21V19"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M19 12H21"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M3 12H5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M17.66 6.34L16.24 7.76"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M7.76 16.24L6.34 17.66"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M17.66 17.66L16.24 16.24"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M7.76 7.76L6.34 6.34"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>

        {/* Moon Icon */}
        <svg
          className={`${styles.icon} ${styles.moonIcon} ${
            isDark ? styles.active : ""
          }`}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      </span>

      {showLabel && (
        <span className={styles.label}>{isDark ? "Dark" : "Light"}</span>
      )}
    </button>
  );
};
